/**
 * obstacles.js — Static arena geometry.
 *
 * Each screen carries a handful of solid slabs — rooftop plant, billboards,
 * pipe runs — that fighters can stand on, hide behind and bounce boards off.
 *
 * They exist mainly to give the beam something to hit. A full-screen instant
 * kill with clear sightlines is decided by whoever lines up first; with cover
 * in the way it becomes a question of position, so the interesting move is to
 * break the firing line rather than win the draw.
 *
 * Layout rules, so the obstacles never fight the rest of the design:
 *   - Ground barriers are short enough to hover straight over. Both fighters
 *     spend most of a match standing on the floor, so cover that floats above
 *     the floor is cover that never comes up; these sit in the middle of the
 *     firing line where a grounded duel actually happens. Being low keeps them
 *     an obstacle to fly over rather than a wall that ends the advance.
 *   - Nothing within EDGE_KEEPOUT of either screen edge, so the advancing
 *     player always has a clean run at the exit and a clear spawn to arrive in.
 *   - Nothing straddling a spawn point.
 *   - Screens 0 and 6 (the goal screens) are left sparse: the final push should
 *     be decided by the fight, not by terrain.
 *
 * Geometry is a pure function of the screen index, so both clients derive the
 * same layout and nothing has to cross the wire in online play.
 */

import { GROUND_Y, BODY_TOP, BODY_BOTTOM, screenWidth, SPAWN } from './constants';

/*
 * Every x and w below is authored in its OWN screen's world coordinates, not in
 * a nominal space that gets stretched to fit.
 *
 * That was the earlier approach, and it is the wrong one now the screens differ
 * in width: stretching a two-piece layout across a room twice as wide gives you
 * the same two pieces with twice the empty floor between them, so the widest
 * rooms — the ones that are supposed to be the hardest to cross — would be the
 * emptiest. A longer room has to be a fuller room or the extra length is just a
 * longer sprint. So the outer screens are authored with more in them.
 */

// Horizontal margin at each screen edge kept completely clear.
const EDGE_KEEPOUT = 110;
// Tallest a floor-standing barrier may be. A grounded fighter's beam leaves the
// muzzle ~46px up, so a barrier has to reach past that to break the sightline —
// but stay low enough to clear with a normal hover.
const BARRIER_HEIGHT = 74;
const BARRIER_TOP = GROUND_Y - BARRIER_HEIGHT;
// A floating ledge that still sits within reach of the fight.
const LEDGE_TOP = GROUND_Y - 150;
// Room left around a spawn point, so nobody materialises inside a slab. Wider
// than a body (13px each side) because arriving flush against a wall is nearly
// as bad as arriving inside one.
const SPAWN_CLEARANCE = 30;

/**
 * Per-screen layouts, in canvas coordinates. `kind` only drives decoration.
 *   slab     — solid rooftop plant / ledge
 *   billboard— tall thin sign, good vertical cover
 *   pipe     — low wide duct, easy to stand on, weak cover
 */
// Shorthands, so a layout row reads as a shape rather than as four numbers.
const vent = (x, w = 54) => ({ x, y: BARRIER_TOP, w, h: BARRIER_HEIGHT, kind: 'vent' });
const slab = (x, w = 150) => ({ x, y: LEDGE_TOP, w, h: 24, kind: 'slab' });
const sign = (x) => ({ x, y: GROUND_Y - 210, w: 28, h: 120, kind: 'billboard' });
const pipe = (x, w = 120) => ({ x, y: GROUND_Y - 46, w, h: 46, kind: 'pipe' });

/**
 * Per-screen layouts, in that screen's own world coordinates. `kind` only drives
 * decoration.
 *   slab     — solid rooftop plant / ledge
 *   billboard— tall thin sign, good vertical cover
 *   pipe     — low wide duct, easy to stand on, weak cover
 *
 * Each pair of screens equidistant from the middle is a mirror of the other, so
 * neither side of the tug-of-war gets the friendlier ground.
 */
const LAYOUTS = [
  // 0 — 1800 wide. Red's goal screen, and Green's last stand. Mirror of 6.
  // Five pieces plus a duct: at this width a sparse room is a long empty
  // sprint, which is the opposite of what the extra length is for. The run to
  // the goal door itself is still left clear (nothing under x=480), so the
  // final push is decided by the fight rather than by a barrier in the gate.
  [vent(480), slab(620, 170), sign(860), vent(1020), slab(1160), pipe(1380)],

  // 1 — 1200 wide. Two barriers to break the lane, a ledge to fight from, and
  // a sign holding the high line. Mirror of 5.
  [vent(300, 46), slab(470), vent(720, 46), sign(900)],

  // 2 — 1000 wide. Off-centre barrier, a billboard covering the high line, and
  // a duct to stand on out near the far spawn. Mirror of 4.
  [vent(300), sign(520), pipe(680, 110)],

  // 3 — 800 wide. The landing stage, unchanged: a ledge to contest, and the
  // floor between the two spawns deliberately left clear. A barrier here would
  // block the ground lane both fighters start on, so neither beams nor kicked
  // boards could cross, and every match would open with the same forced climb.
  [slab(340, 120)],

  // 4 — mirror of screen 2.
  [pipe(210, 110), sign(452), vent(646)],

  // 5 — mirror of screen 1.
  [sign(272), vent(434, 46), slab(580), vent(854, 46)],

  // 6 — mirror of screen 0. Green's goal screen.
  [pipe(300), slab(490), vent(726), sign(912), slab(1010, 170), vent(1266)],
];

// Precompute the bounds each obstacle is tested against, and enforce the layout
// rules above rather than trusting the table to stay correct by inspection. A
// piece that breaks a rule is dropped, not drawn wrong.
/** Would a fighter standing at this spawn be inside the piece? */
const straddlesASpawn = (o, screen) =>
  Object.keys(SPAWN).some((slot) => {
    const sx = SPAWN[slot].xFraction * screenWidth(screen);
    const sy = SPAWN[slot].y;
    return (
      sx + SPAWN_CLEARANCE > o.x
      && sx - SPAWN_CLEARANCE < o.x + o.w
      && sy + BODY_TOP < o.y + o.h
      && sy + BODY_BOTTOM > o.y
    );
  });

const SCREENS = LAYOUTS.map((layout, screen) => {
  const width = screenWidth(screen);
  return layout
    .filter((o) => {
      const clearOfEdges =
        o.x >= EDGE_KEEPOUT && o.x + o.w <= width - EDGE_KEEPOUT;
      const aboveFloor = o.y + o.h <= GROUND_Y;
      // Anything resting on the floor has to be hoppable.
      const restsOnFloor = o.y + o.h >= GROUND_Y - 4;
      const hoppable = !restsOnFloor || o.h <= BARRIER_HEIGHT;
      return clearOfEdges && aboveFloor && hoppable && !straddlesASpawn(o, screen);
    })
    .map((o) => ({
      ...o,
      left: o.x,
      right: o.x + o.w,
      top: o.y,
      bottom: o.y + o.h,
    }));
});

/**
 * @param {number} screen Screen index (0..6).
 * @returns {Array<object>} obstacles with {x,y,w,h,kind,left,right,top,bottom}.
 */
export function getObstacles(screen) {
  return SCREENS[screen] || [];
}

/**
 * Would moving horizontally from here run into something?
 *
 * Used by the AI, which decides where to go without any notion of terrain;
 * this lets the result be corrected into a hop rather than teaching every
 * branch of the decision tree about geometry.
 *
 * @param {number} screen    Screen index.
 * @param {number} x         Fighter's x.
 * @param {number} feetY     Fighter's feet (player.y).
 * @param {number} direction +1 moving right, -1 moving left.
 * @param {number} [lookahead] How far ahead to probe, in px.
 * @returns {boolean} true if an obstacle stands in the way.
 */
export function isBlockedAhead(screen, x, feetY, direction, lookahead = 44) {
  const probeX = x + direction * lookahead;
  return getObstacles(screen).some(
    (o) =>
      probeX > o.left - 12 &&
      probeX < o.right + 12 &&
      // Feet below the top edge means we would hit its side, not clear it.
      feetY > o.top + 2 &&
      feetY <= o.bottom + 2
  );
}

/**
 * How far a horizontal ray travels from `originX` before an obstacle stops it.
 *
 * @param {number} screen    Screen index.
 * @param {number} originX   Where the ray starts.
 * @param {number} y         Ray height.
 * @param {number} direction +1 travelling right, -1 travelling left.
 * @returns {number} x of the first blocking edge, or the screen edge if clear.
 */
export function raycastX(screen, originX, y, direction) {
  let hit = direction > 0 ? screenWidth(screen) : 0;

  getObstacles(screen).forEach((o) => {
    if (y < o.top || y > o.bottom) return; // ray passes above or below
    if (direction > 0) {
      if (o.left >= originX && o.left < hit) hit = o.left;
    } else if (o.right <= originX && o.right > hit) {
      hit = o.right;
    }
  });

  return hit;
}
